import type * as React from 'react'

import { clsx } from 'clsx'

import {
  XIcon,
  PlayFilledIcon,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import { Upload } from '@buffer-mono/uploader'
import type { Uploader } from '@buffer-mono/uploader'

import { escapeParens } from '~publish/legacy/composer/composer/utils/StringUtils'
import { useAppDispatch } from '~publish/legacy/store'
import { getQueueResizedImageUrl } from '~publish/legacy/utils/bufferImages'

import { CanvaEditTag } from './CanvaEditTag'
import CircularUploadIndicator from '../../composer/composer/components/progress-indicators/CircularUploadIndicator'
import { uploadRemoved } from '../state/slice'

import styles from './Thumbnail.module.css'

import type { JSX } from 'react'

const getPreviewUrl = (upload: Upload): string | undefined => {
  if (Upload.isCompleted(upload)) return Upload.getThumbnailUrl(upload)
  // Pending uploads carry the Uppy-generated client-side preview blob URL in
  // `thumbnailUrl` (set in UppyFile.toUpload). Uppy populates it for images
  // only, so other media has no preview until its upload completes.
  return upload.thumbnailUrl
}

export const Thumbnail = ({
  upload,
  openPreview,
  uploader,
  readOnly = false,
}: {
  upload: Upload
  openPreview: (upload: Upload | null) => void
  uploader: Pick<Uploader, 'removeUpload' | 'addFilesFromUrls'>
  /** Show the media without the remove affordance. */
  readOnly?: boolean
}): JSX.Element => {
  const dispatch = useAppDispatch()

  const isPending = Upload.isPending(upload)

  const tooltipText = isPending
    ? 'Uploading…'
    : Upload.canAddDescription(upload)
      ? 'Preview and add description'
      : 'Preview'

  const onThumbnailClick = (): void => {
    if (isPending) return
    openPreview(upload)
  }

  const onDeleteClick = (): void => {
    uploader.removeUpload(upload)

    // REVIEW: this should be handled by the uploader
    dispatch(uploadRemoved(upload.id))
  }

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (isPending) return
    if (event.key === 'Enter' || event.key === ' ') {
      openPreview(upload)
    }
  }

  const previewUrl = getPreviewUrl(upload)

  return (
    <Tooltip content={tooltipText}>
      <div
        data-testid="media-manager-thumbnail"
        className={clsx(styles.thumbnail, isPending && styles.pending)}
      >
        {!readOnly && (
          <UnstyledButton onClick={onDeleteClick} aria-label="Remove media">
            <XIcon className={styles.deleteIcon} />
          </UnstyledButton>
        )}
        {Upload.isVideo(upload) && <div className={styles.thumbnailOverlay} />}
        {previewUrl && (
          <>
            {Upload.isDocumentUpload(upload) ? (
              <img
                alt={upload.alt || upload.name}
                src={getQueueResizedImageUrl(escapeParens(previewUrl))}
                crossOrigin={'anonymous'}
                onClick={onThumbnailClick}
                role="presentation"
              />
            ) : (
              <img
                src={previewUrl}
                alt={upload.alt || upload.name}
                onClick={onThumbnailClick}
                role="presentation"
              />
            )}
          </>
        )}
        {!isPending && <CanvaEditTag upload={upload} uploader={uploader} />}
        {Upload.isVideo(upload) && !isPending && (
          <PlayFilledIcon className={styles.playIcon} />
        )}
        {isPending && (
          <div
            className={styles.progressOverlay}
            data-testid="media-thumbnail-progress"
          >
            <CircularUploadIndicator
              size={54}
              progress={Upload.getPercentage(upload)}
            />
          </div>
        )}
        <VisuallyHidden
          as="button"
          onClick={onThumbnailClick}
          onKeyDown={onKeyDown}
          aria-disabled={isPending}
        />
      </div>
    </Tooltip>
  )
}
